import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";

// =============================================================
// Hold-harmless / legal copy — now CMS-managed.
// Documents live in MongoDB `site_legal_docs` and are seeded with the
// canonical wording previously hardcoded here. Admins can edit each
// document via the "Legal documents" admin tab. Public components
// (PublicDisclaimer, VolunteerHoldHarmless, ContractorHoldHarmless,
// ContractorInsuranceRequirement) fetch live content with a graceful
// loading fallback.
//
// Used on:
//   • About / Governance page  (PublicDisclaimer)
//   • Volunteer signup form    (VolunteerHoldHarmless + checkbox)
//   • Contractor application   (ContractorHoldHarmless + checkbox)
// =============================================================

const renderInlineMarkdown = (text) => {
    if (!text) return null;
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) => (
        p.startsWith("**") && p.endsWith("**")
            ? <strong key={i} className="text-[#1C252E]">{p.slice(2, -2)}</strong>
            : <React.Fragment key={i}>{p}</React.Fragment>
    ));
};

// Module-level cache keyed by document key so VolunteerForm, ContractorForm
// and About don't each trigger a separate API call on mount.
const _cache = new Map();
const _inflight = new Map();

const fetchLegalDoc = (key) => {
    if (_cache.has(key)) return Promise.resolve(_cache.get(key));
    if (_inflight.has(key)) return _inflight.get(key);
    const p = api.get(`/legal-docs/${key}`)
        .then(({ data }) => { _cache.set(key, data); _inflight.delete(key); return data; })
        .catch((err) => { _inflight.delete(key); throw err; });
    _inflight.set(key, p);
    return p;
};

// Invalidate the in-memory cache (used by admin editor after saving so the
// next public page-render picks up the fresh copy without a hard refresh).
export const invalidateLegalDocCache = (key) => {
    if (key) _cache.delete(key);
    else _cache.clear();
};

const useLegalDoc = (key) => {
    const [doc, setDoc] = useState(_cache.get(key) || null);
    const reload = useCallback(() => {
        fetchLegalDoc(key).then(setDoc).catch(() => setDoc(null));
    }, [key]);
    useEffect(() => { reload(); }, [reload]);
    return doc;
};

const LegalParagraphs = ({ paragraphs, textClass = "text-sm" }) => (
    <>
        {paragraphs.map((p, i) => {
            const baseClass = `${textClass} leading-relaxed`;
            if (p.style === "italic") {
                return <p key={i} className={`text-[11px] italic ${textClass === "text-sm" ? "text-[#4A5568]" : "text-[#4A5568]"}`}>{renderInlineMarkdown(p.text)}</p>;
            }
            if (p.style === "small") {
                return <p key={i} className={`text-xs text-[#4A5568] leading-relaxed`}>{renderInlineMarkdown(p.text)}</p>;
            }
            return <p key={i} className={`${baseClass} text-[#4A5568]`}>{renderInlineMarkdown(p.text)}</p>;
        })}
    </>
);

// ---------- Public exports (CMS-backed) ----------
export const PUBLIC_DISCLAIMER_TITLE = "Volunteer and Contractor Responsibility Disclaimer";
export const VOLUNTEER_HOLD_HARMLESS_TITLE = "Volunteer Acknowledgment, Release, and Hold Harmless";
export const CONTRACTOR_HOLD_HARMLESS_TITLE = "Contractor / Vendor Hold Harmless, Insurance, and Responsibility Clause";

export const PublicDisclaimer = () => {
    const doc = useLegalDoc("public_disclaimer");
    if (!doc) return <p className="text-sm text-[#4A5568] italic">Loading disclaimer…</p>;
    return (
        <div className="space-y-3" data-testid="legal-doc-public_disclaimer">
            <LegalParagraphs paragraphs={doc.paragraphs || []} textClass="text-sm" />
        </div>
    );
};

export const VolunteerHoldHarmless = () => {
    const doc = useLegalDoc("volunteer_agreement");
    if (!doc) return <p className="text-xs text-[#4A5568] italic">Loading…</p>;
    return (
        <div className="space-y-3" data-testid="legal-doc-volunteer_agreement">
            <LegalParagraphs paragraphs={doc.paragraphs || []} textClass="text-xs" />
        </div>
    );
};

export const ContractorHoldHarmless = () => {
    const doc = useLegalDoc("contractor_agreement");
    if (!doc) return <p className="text-xs text-[#4A5568] italic">Loading…</p>;
    return (
        <div className="space-y-3" data-testid="legal-doc-contractor_agreement">
            <LegalParagraphs paragraphs={doc.paragraphs || []} textClass="text-xs" />
        </div>
    );
};

export const ContractorInsuranceRequirement = () => {
    const doc = useLegalDoc("contractor_insurance");
    if (!doc) return null;
    const first = (doc.paragraphs || [])[0];
    if (!first) return null;
    return (
        <p className="text-xs text-[#4A5568] leading-relaxed" data-testid="legal-doc-contractor_insurance">
            {renderInlineMarkdown(first.text)}
        </p>
    );
};

// ---------- Collapsible wrapper (unchanged) ----------
export const Collapsible = ({ title, defaultOpen = false, children, testid }) => (
    <details className="bg-[#F4F1EA]/60 border border-black/10 rounded-xl px-4 py-3 group" open={defaultOpen} data-testid={testid}>
        <summary className="cursor-pointer text-xs uppercase tracking-[0.18em] font-bold text-[#1C252E] list-none flex items-center justify-between">
            <span>{title}</span>
            <span className="text-[#E53935] text-[10px] group-open:hidden">+ Read</span>
            <span className="text-[#E53935] text-[10px] hidden group-open:inline">− Hide</span>
        </summary>
        <div className="mt-3">{children}</div>
    </details>
);
